// Importing companies into Sales and Potential clients — TODO T63 (9 Sep 2026).
//
// TWO CALLS, NOT THREE. T43's candidate import polls, because the record it creates is written by
// kadmon's side and takes a moment. A company is the brain's own to write, so the confirm here
// answers with what happened to every row and the dialog is done.
//
// `status` — the tab the button was pressed on — rides on both calls, and is the only thing that
// says where a new company lands. Nothing in the file can (design doc § T63.1).
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  ClientImportConfirmResponse,
  ClientImportOptionsResponse,
  ClientImportPreviewResponse,
  ClientImportRowFields,
} from '@vera/bff';
import type { ClientStatus } from '@vera/db/client-status';
import { API_PREFIX, type RequestFn } from './core.ts';
import { KEYS, useConsoleMutation } from './mutation.ts';
import { useRequest } from './provider.tsx';

export interface ClientImportPreviewInput {
  status: ClientStatus;
  /** The file's name, for the dialog alone — nothing records it. */
  name: string;
  /** A CSV's text, read in the browser — or a workbook (`.xlsx`) as base64. One of the two. */
  text?: string;
  workbook?: string;
  country: string | null;
  /**
   * T113a: names the preview called new, pointed by the recruiter at one in the brain — a company
   * by the name the file gave, a contact by their company's id and the name the file gave.
   */
  links?: {
    companies: { name: string; clientId: string }[];
    contacts: { clientId: string; name: string; contactId: string }[];
  };
}

export interface ClientImportConfirmInput {
  status: ClientStatus;
  rows: ClientImportRowFields[];
}

export const clientImportApi = (request: RequestFn) => ({
  options: () => request<ClientImportOptionsResponse>(`${API_PREFIX}/clients/import/options`),
  // T160: both carry the page's release, so a page from another one is told to reload.
  preview: (input: ClientImportPreviewInput) =>
    request<ClientImportPreviewResponse>(`${API_PREFIX}/clients/import/preview`, {
      method: 'POST',
      body: { ...input, release: request.release ?? null },
    }),
  confirm: (input: ClientImportConfirmInput) =>
    request<ClientImportConfirmResponse>(`${API_PREFIX}/clients/import`, {
      method: 'POST',
      body: { ...input, release: request.release ?? null },
    }),
});

export function useClientImportOptions(
  enabled: boolean,
): UseQueryResult<ClientImportOptionsResponse> {
  const request = useRequest();
  return useQuery({
    queryKey: ['clientImportOptions'],
    queryFn: () => clientImportApi(request).options(),
    enabled,
    staleTime: Infinity,
  });
}

/** A preview is a mutation in shape — a POST with a body — and a read in effect: it invalidates nothing. */
export const useClientImportPreview = () =>
  useConsoleMutation(
    (request, input: ClientImportPreviewInput) => clientImportApi(request).preview(input),
    [],
  );

/** The confirm moves every CRM list at once, which is exactly what a client write invalidates. */
export const useConfirmClientImport = () =>
  useConsoleMutation(
    (request, input: ClientImportConfirmInput) => clientImportApi(request).confirm(input),
    KEYS.client,
  );
